import '@justgiving/carepack-forms/forms.css';
import '@justgiving/carepack-design-tokens/dist/colors.cssmodules.css';
import '@justgiving/carepack-design-tokens/dist/layout.cssmodules.css';
import '@justgiving/carepack-design-tokens/dist/typography.cssmodules.css';
import '@justgiving/loading/src/Loading.scss';
import '@justgiving/carepack-typography';
import '@justgiving/carepack-core';
import '@justgiving/carepack-dialog/dialog.css';
import '@justgiving/carepack-toast/toast.css';
import '@justgiving/carepack-chips/chips.css';
import '@justgiving/carepack-snackbar/snackbar.css';
import '@justgiving/carepack-drawer/styles.css';
import '@justgiving/carepack-avatar/avatar.css';

import React, { Suspense, PropsWithChildren, useMemo } from 'react';

import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useSearchParams,
  useRouteError,
} from 'react-router-dom';

import Loading from '@justgiving/loading';
import { PageComponentsProvider } from '@justgiving/page-components';

import { PageType } from '../../../types/graphql';
import { ErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary';
import { AuthProvider, Permissions } from '../../context/Auth';
import { PathInfoProvider } from '../../context/PathInfo';
import { usePathInfo } from '../../hooks/usePathInfo';
import { CharityAuthoriser } from '../../components/Authoriser/CharityAuthoriser';
import i18n from 'i18next';
import './App.scss';
import { OptimizelyProvider } from '../../components/OptimizelyProvider/OptimizelyProvider';
import { I18nextProvider } from 'react-i18next';
import { TrackingProvider } from '@justgiving/react-tracking';
import { Toaster } from '@justgiving/carepack-toast';
import config from '@/config';
import ErrorPage from '@/components/ErrorPage/ErrorPage';
import { CustomError } from '@/components/ErrorBoundary/errors';

const analytics = {
  track: (data: Record<string, unknown>) => {
    window.dataLayer.push(data);
  },
};

const Auth = ({ children }: PropsWithChildren) => {
  const { pageType } = usePathInfo();

  switch (pageType) {
    case PageType.Charity:
      return (
        <CharityAuthoriser>
          <AuthProvider permissions={[Permissions.isValidCharityUser]}>
            {children}
          </AuthProvider>
        </CharityAuthoriser>
      );
    case PageType.Campaign:
      return (
        <CharityAuthoriser>
          <AuthProvider permissions={[Permissions.IsCampaignAdmin]}>
            {children}
          </AuthProvider>
        </CharityAuthoriser>
      );
    case PageType.OnePage:
    case PageType.Crowdfunding:
    default:
      return (
        <AuthProvider permissions={[Permissions.IsPageOwner]}>
          {children}
        </AuthProvider>
      );
  }
};

const fundraisingPrefixes = ['fundraising', 'page'];

const RouteErrorBoundary = () => {
  const error = useRouteError();
  return <ErrorPage error={error as Error} />;
};

export const createAppRouter = () =>
  createBrowserRouter([
    ...fundraisingPrefixes.map((prefix) => ({
      path: `${prefix}/:slug/admin`,
      errorElement: <RouteErrorBoundary />,
      lazy: async () => ({
        Component: (
          await import('../Fundraising/FundraisingRoot/FundraisingRoot')
        ).FundraisingRoot,
      }),
      children: [
        {
          index: true,
          element: <Navigate to="edit" replace />,
        },
        {
          path: 'edit',
          lazy: async () => ({
            Component: (await import('@/pages/shared/Edit/EditPage')).default,
          }),
        },
        {
          path: 'donations/offline',
          lazy: async () => ({
            Component: (
              await import(
                '@/pages/shared/Donations/components/OfflineDonationWrapper/OfflineDonationWrapper'
              )
            ).OfflineDonationWrapper,
          }),
        },
        {
          path: 'donations',
          lazy: async () => {
            const DonationsPage = (
              await import('@/pages/Fundraising/Donations')
            ).Donations;
            const NewDonationsPage = (
              await import('@/pages/shared/Donations/DonationsPage')
            ).default;

            return {
              Component: () => {
                const [searchParams] = useSearchParams();
                const useNewDonationsPage =
                  config.features.useNewDonationsPage ||
                  searchParams.has('useNewDonationsPage');

                return useNewDonationsPage ? (
                  <NewDonationsPage />
                ) : (
                  <DonationsPage />
                );
              },
            };
          },
        },
        {
          path: 'donations/all',
          lazy: async () => ({
            Component: (
              await import('@/pages/shared/AllDonations/AllDonationsPage')
            ).default,
          }),
        },
        {
          path: 'donations/offline-all',
          lazy: async () => ({
            Component: (
              await import(
                '@/pages/shared/AllOfflineDonations/AllOfflineDonations'
              )
            ).default,
          }),
        },
        {
          path: 'updates',
          lazy: async () => ({
            Component: (await import('@/pages/shared/Updates/UpdatesPage'))
              .default,
          }),
        },
        {
          path: 'share',
          lazy: async () => ({
            Component: (await import('@/pages/shared/Share/Share')).default,
          }),
        },
        {
          path: 'settings/*',
          lazy: async () => {
            return {
              Component: (await import('@/pages/shared/Settings/SettingsPage'))
                .default,
            };
          },
        },
      ],
    })),
    {
      path: 'crowdfunding/:slug/admin',
      errorElement: <RouteErrorBoundary />,
      lazy: async () => ({
        Component: (await import('../Crowdfunding/CrowdfundingRoot')).default,
      }),
      children: [
        {
          index: true,
          lazy: async () => ({
            Component: (await import('@/pages/Crowdfunding/Overview/Overview'))
              .default,
          }),
        },
        {
          path: 'theme/:themeId',
          lazy: async () => ({
            Component: (await import('@/pages/Crowdfunding/Overview/Overview'))
              .default,
          }),
        },
        {
          path: 'livestream',
          lazy: async () => ({
            Component: (await import('@/pages/Crowdfunding/Livestream'))
              .default,
          }),
        },
        {
          path: 'donations',

          lazy: async () => {
            const DonationsPage = (
              await import('@/pages/Crowdfunding/Donations/Donations')
            ).default;
            const NewDonationsPage = (
              await import('@/pages/shared/Donations/DonationsPage')
            ).default;

            return {
              Component: () => {
                const [searchParams] = useSearchParams();
                const useNewDonationsPage =
                  config.features.useNewDonationsPage ||
                  searchParams.has('useNewDonationsPage');

                return useNewDonationsPage ? (
                  <NewDonationsPage />
                ) : (
                  <DonationsPage />
                );
              },
            };
          },
        },
        {
          path: 'donations/all',
          lazy: async () => ({
            Component: (
              await import('@/pages/shared/AllDonations/AllDonationsPage')
            ).default,
          }),
        },
        {
          path: 'edit',
          lazy: async () => ({
            Component: (await import('@/pages/shared/Edit/EditPage')).default,
          }),
        },
        {
          path: 'updates',
          lazy: async () => ({
            Component: (await import('@/pages/shared/Updates/UpdatesPage'))
              .default,
          }),
        },
        {
          path: 'share',
          lazy: async () => ({
            Component: (await import('@/pages/shared/Share/Share')).default,
          }),
        },
        {
          path: 'settings',
          lazy: async () => ({
            Component: (await import('@/pages/shared/Settings/SettingsPage'))
              .default,
          }),
        },
      ],
    },
    {
      path: 'campaign/admin',
      errorElement: <RouteErrorBoundary />,
      lazy: async () => {
        return {
          Component: (await import('../Campaign/CampaignRoot/CampaignRoot'))
            .CampaignRoot,
        };
      },
    },
    {
      path: 'charity/:slug/admin',
      errorElement: <RouteErrorBoundary />,
      lazy: async () => {
        return {
          Component: (await import('../Charity/CharityRoot/CharityRoot'))
            .CharityRoot,
        };
      },
      children: [
        {
          index: true,
          element: <Navigate to="edit" replace />,
        },
        {
          path: 'edit',
          lazy: async () => ({
            Component: (await import('@/pages/Charity/EditPage/EditPage'))
              .default,
          }),
        },
      ],
    },
    {
      path: '*',
      element: (
        <ErrorPage
          error={new CustomError({ message: 'Page not found', code: 404 })}
        />
      ),
    },
  ]);

function App() {
  const router = useMemo(() => createAppRouter(), []);

  return (
    <ErrorBoundary>
      <PathInfoProvider>
        <Auth>
          <TrackingProvider analytics={analytics}>
            <OptimizelyProvider>
              <Suspense fallback={<Loading />}>
                <I18nextProvider i18n={i18n}>
                  <PageComponentsProvider>
                    <RouterProvider router={router} />
                  </PageComponentsProvider>
                </I18nextProvider>
              </Suspense>
            </OptimizelyProvider>
          </TrackingProvider>
        </Auth>
      </PathInfoProvider>
      <Toaster />
    </ErrorBoundary>
  );
}

export default App;
